/**
 * Offline Database — IndexedDB wrapper for local data caching
 * Provides instant data access when offline and syncs when online.
 */

const DB_NAME = 'majelani-offline';
const DB_VERSION = 1;

// Stores we cache locally
const STORES = ['customers', 'invoices', 'vendors', 'expenses', 'accounts', 'payments', 'bills', 'settings'] as const;
type StoreName = typeof STORES[number];

let db: IDBDatabase | null = null;

/**
 * Open/initialize the IndexedDB database
 */
function openDB(): Promise<IDBDatabase> {
  if (db) return Promise.resolve(db);

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const database = (event.target as IDBOpenDBRequest).result;
      STORES.forEach(store => {
        if (!database.objectStoreNames.contains(store)) {
          database.createObjectStore(store, { keyPath: 'id' });
        }
      });
      // Outbox for pending changes (offline mutations)
      if (!database.objectStoreNames.contains('_outbox')) {
        const outbox = database.createObjectStore('_outbox', { keyPath: 'id', autoIncrement: true });
        outbox.createIndex('timestamp', 'timestamp');
        outbox.createIndex('synced', 'synced');
      }
      // Metadata store for timestamps
      if (!database.objectStoreNames.contains('_meta')) {
        database.createObjectStore('_meta', { keyPath: 'key' });
      }
    };

    request.onsuccess = () => {
      db = request.result;
      resolve(db);
    };

    request.onerror = () => reject(request.error);
  });
}

/**
 * Get all items from a store
 */
export async function getAll(store: StoreName): Promise<any[]> {
  const database = await openDB();
  return new Promise((resolve, reject) => {
    const tx = database.transaction(store, 'readonly');
    const request = tx.objectStore(store).getAll();
    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Get a single item by ID
 */
export async function getById(store: StoreName, id: string): Promise<any | null> {
  const database = await openDB();
  return new Promise((resolve, reject) => {
    const tx = database.transaction(store, 'readonly');
    const request = tx.objectStore(store).get(id);
    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Save multiple items to a store (replaces all)
 */
export async function putAll(store: StoreName, items: any[]): Promise<void> {
  const database = await openDB();
  return new Promise((resolve, reject) => {
    const tx = database.transaction(store, 'readwrite');
    const objectStore = tx.objectStore(store);
    // Clear existing and add new
    objectStore.clear();
    items.forEach(item => {
      if (item.id) objectStore.put(item);
    });
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Save a single item
 */
export async function put(store: StoreName, item: any): Promise<void> {
  const database = await openDB();
  return new Promise((resolve, reject) => {
    const tx = database.transaction(store, 'readwrite');
    tx.objectStore(store).put(item);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Delete a single item
 */
export async function remove(store: StoreName, id: string): Promise<void> {
  const database = await openDB();
  return new Promise((resolve, reject) => {
    const tx = database.transaction(store, 'readwrite');
    tx.objectStore(store).delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Get last sync timestamp for a store
 */
export async function getLastSync(store: StoreName): Promise<number> {
  const database = await openDB();
  return new Promise((resolve) => {
    const tx = database.transaction('_meta', 'readonly');
    const request = tx.objectStore('_meta').get(`lastSync_${store}`);
    request.onsuccess = () => resolve(request.result?.value || 0);
    request.onerror = () => resolve(0);
  });
}

/**
 * Set last sync timestamp
 */
export async function setLastSync(store: StoreName): Promise<void> {
  const database = await openDB();
  return new Promise((resolve, reject) => {
    const tx = database.transaction('_meta', 'readwrite');
    tx.objectStore('_meta').put({ key: `lastSync_${store}`, value: Date.now() });
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Add an operation to the outbox (for offline mutations)
 */
export async function addToOutbox(operation: {
  store: StoreName;
  method: 'POST' | 'PUT' | 'DELETE';
  url: string;
  body?: any;
}): Promise<void> {
  const database = await openDB();
  return new Promise((resolve, reject) => {
    const tx = database.transaction('_outbox', 'readwrite');
    tx.objectStore('_outbox').add({
      ...operation,
      timestamp: Date.now(),
      synced: false,
    });
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Get all pending (unsynced) outbox items
 */
export async function getPendingOutbox(): Promise<any[]> {
  const database = await openDB();
  return new Promise((resolve, reject) => {
    const tx = database.transaction('_outbox', 'readonly');
    const request = tx.objectStore('_outbox').getAll();
    request.onsuccess = () => {
      const items = (request.result || []).filter((item: any) => !item.synced);
      resolve(items);
    };
    request.onerror = () => reject(request.error);
  });
}

/**
 * Mark outbox item as synced
 */
export async function markSynced(id: number): Promise<void> {
  const database = await openDB();
  return new Promise((resolve, reject) => {
    const tx = database.transaction('_outbox', 'readwrite');
    const store = tx.objectStore('_outbox');
    const request = store.get(id);
    request.onsuccess = () => {
      if (request.result) {
        store.put({ ...request.result, synced: true });
      }
    };
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Clear all synced items from outbox
 */
export async function clearSyncedOutbox(): Promise<void> {
  const database = await openDB();
  return new Promise((resolve, reject) => {
    const tx = database.transaction('_outbox', 'readwrite');
    const store = tx.objectStore('_outbox');
    const request = store.getAll();
    request.onsuccess = () => {
      (request.result || []).forEach((item: any) => {
        if (item.synced) store.delete(item.id);
      });
    };
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Get outbox count (pending items)
 */
export async function getOutboxCount(): Promise<number> {
  const pending = await getPendingOutbox();
  return pending.length;
}
